import Alert from "@mui/material/Alert";
import { useMemo, useState } from "react";
import {
	Box,
	Button,
	Stack,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from "@mui/material";
import { useUserStore } from "../../stores/user.store";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import {
	useApproveEventInstance,
	useRejectEventInstance,
} from "../../hooks/useEventInstance";
import { ReviewDetail } from "@/domain/eventInstance.interface";
import {
	CompoundView,
	FormDisplay,
	GenericPlaceholder,
	PhotoViewer,
	SubmissionDetailsModal,
	VideoPlayer,
} from "../../components/SubmissionViewer";
import { ApproveDialog } from "./ApproveDialog";
import { RejectDialog } from "./RejectDialog";

interface Props {
	open: boolean;
	enrollmentId: string | undefined;
	instanceId: string | undefined;
	onClose: () => void;
}

/** Does this submission hold a captured private video? Mirrors SubmissionDetailsModal. */
const hasCapturedVideo = (detail: ReviewDetail): boolean => {
	const captured = detail.submittedData?.capturedVideo as
		| { key?: unknown }
		| undefined;
	return typeof captured?.key === "string" && captured.key.length > 0;
};

/**
 * 🔴 SIMS-8319 — does a decision on THIS submission require the video to have been played first?
 *
 * Screen `3f`: "Approve and Reject stay disabled until the video has been played. A decision
 * cannot be recorded on media the reviewer has not seen." No acceptance criterion carries this;
 * the frame states it and the Product Owner ratified it (portal Q-6, option A, 2026-10-01).
 *
 * It applies ONLY where there is a captured video to watch. A PHOTOS or FORM submission has no
 * playback, so gating it would lock the reviewer out of a decision they can perfectly well take.
 */
const requiresPlayback = (detail: ReviewDetail, stepIndex: number): boolean => {
	const step = detail.compoundSteps?.[stepIndex];
	if (step) return step.validationType === "CAPTURE_VIDEO";
	return detail.validationType === "CAPTURE_VIDEO" && hasCapturedVideo(detail);
};

const renderStepBody = (
	detail: ReviewDetail,
	stepIndex: number,
	reviewerId: string,
	onPlayed: () => void,
): JSX.Element => {
	const compound = detail.compoundSteps;
	if (compound?.length) {
		const step = compound[stepIndex];
		switch (step.validationType) {
			case "PHOTOS":
				return <PhotoViewer photos={step.photos ?? []} mode="review" />;
			case "FORM":
				return (
					<FormDisplay
						formConfig={step.formConfig}
						responses={step.formResponses}
						mode="review"
					/>
				);
			case "CAPTURE_VIDEO":
				// SIMS-8319 AC2 — a signed link per submission, and onPlayed is what unlocks the
				// decision buttons below (portal Q-6, PO chose option A).
				return (
					<VideoPlayer
						mode="review"
						enrollmentId={detail.enrollmentId}
						instanceId={detail.instanceId}
						reviewerId={reviewerId}
						hasPrivateMedia={hasCapturedVideo(detail)}
						onPlayed={onPlayed}
					/>
				);
			case "WATCH_VIDEO":
				return <VideoPlayer mode="review" />;
			default:
				return (
					<GenericPlaceholder
						validationType={step.validationType}
						mode="review"
					/>
				);
		}
	}

	switch (detail.validationType) {
		case "PHOTOS":
			return <PhotoViewer photos={detail.photos ?? []} mode="review" />;
		case "FORM":
			return (
				<FormDisplay
					formConfig={detail.formConfig}
					responses={detail.formResponses}
					mode="review"
				/>
			);
		case "CAPTURE_VIDEO":
			return (
				<VideoPlayer
					mode="review"
					enrollmentId={detail.enrollmentId}
					instanceId={detail.instanceId}
					reviewerId={reviewerId}
					hasPrivateMedia={hasCapturedVideo(detail)}
					onPlayed={onPlayed}
				/>
			);
		case "WATCH_VIDEO":
			return <VideoPlayer mode="review" />;
		case "COMPOUND":
			// Defensive — compound but no resolved steps came through.
			return <CompoundView steps={detail.compoundSteps ?? []} mode="review" />;
		default:
			return (
				<GenericPlaceholder
					validationType={detail.validationType}
					mode="review"
				/>
			);
	}
};

export const ReviewModal = ({
	open,
	enrollmentId,
	instanceId,
	onClose,
}: Props) => {
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const reviewerId = userInfo?.id ?? "";

	const [stepIndex, setStepIndex] = useState(0);
	/**
	 * Which steps the reviewer has actually PLAYED — SIMS-8319, portal Q-6 (PO: option A).
	 *
	 * Keyed by step so a compound submission cannot be approved after watching only one of its
	 * videos. Reset with the modal, deliberately: "has been played" is a statement about this
	 * review sitting, not a durable fact about the submission.
	 */
	const [playedSteps, setPlayedSteps] = useState<Record<number, boolean>>({});
	const [approveOpen, setApproveOpen] = useState(false);
	const [rejectOpen, setRejectOpen] = useState(false);

	const approveMutation = useApproveEventInstance(reviewerId);
	const rejectMutation = useRejectEventInstance(reviewerId);

	/**
	 * 🔴 Is a decision blocked because the reviewer has not watched the video?
	 *
	 * Screen `3f`, ratified by the PO as portal Q-6 option A: "Approve and Reject stay disabled
	 * until the video has been played. A decision cannot be recorded on media the reviewer has not
	 * seen." Approval pays the plan's configured amount and rejection is FINAL (G11, PRD FR-49),
	 * so this is the control that stops either being recorded against a video nobody opened.
	 *
	 * The bar is STARTED playback, not a full watch — the frame says "has been played", and
	 * requiring a complete watch of a three-minute video on every submission would be a different
	 * (and much heavier) rule than the one drawn. Stated in Q-6 and not objected to.
	 */
	const decisionBlocked = (detail: ReviewDetail): boolean =>
		requiresPlayback(detail, stepIndex) && !playedSteps[stepIndex];

	const handleClose = (): void => {
		setStepIndex(0);
		setPlayedSteps({});
		setApproveOpen(false);
		setRejectOpen(false);
		onClose();
	};

	return (
		<>
			<SubmissionDetailsModal
				open={open}
				enrollmentId={enrollmentId}
				instanceId={instanceId}
				onClose={handleClose}
				renderBody={(detail) => (
					<ReviewBody
						detail={detail}
						stepIndex={stepIndex}
						onStep={setStepIndex}
						reviewerId={reviewerId}
						onPlayed={() => setPlayedSteps((s) => ({ ...s, [stepIndex]: true }))}
					/>
				)}
				renderActions={(detail) => {
					// SIMS-8482 AC10 — a rejection cannot be reversed, so once a
					// decision exists no action may offer to undo, reverse or
					// re-decide it. The guard lives HERE rather than in each caller:
					// it held only because three separate call sites each remembered
					// to route decided items to a read-only view, and one new caller
					// would have regressed it with every test still green.
					if (detail.status !== "PENDING_APPROVAL") {
						return null;
					}
					const stepCount = detail.compoundSteps?.length ?? 1;
					const onLastStep = stepIndex >= stepCount - 1;
					return (
						<Stack
							direction="row"
							spacing={1}
							justifyContent="space-between"
							sx={{
								p: 2,
								borderTop: "1px solid",
								borderColor: "divider",
							}}
						>
							{/* The frame states the reason, so a disabled button is never unexplained. */}
							{decisionBlocked(detail) && (
								<Alert
									severity="info"
									sx={{ width: "100%", mb: 1 }}
									data-testid="review-playback-required"
								>
									Approve and Reject stay disabled until the video has been
									played. A decision cannot be recorded on media the reviewer has
									not seen.
								</Alert>
							)}
							<Button
								color="error"
								variant="outlined"
								onClick={() => setRejectOpen(true)}
								disabled={decisionBlocked(detail)}
								data-testid="review-reject"
							>
								Reject
							</Button>
							<Stack direction="row" spacing={1}>
								{stepCount > 1 && (
									<Button
										onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
										disabled={stepIndex === 0}
									>
										← Previous
									</Button>
								)}
								{stepCount > 1 && !onLastStep && (
									<Button
										variant="contained"
										onClick={() =>
											setStepIndex((i) => Math.min(stepCount - 1, i + 1))
										}
									>
										Next →
									</Button>
								)}
								{onLastStep && (
									<Button
										variant="contained"
										color="success"
										onClick={() => setApproveOpen(true)}
										disabled={decisionBlocked(detail)}
										data-testid="review-approve"
									>
										Approve
									</Button>
								)}
							</Stack>
							<ApproveDialog
								open={approveOpen}
								eventName={detail.eventName}
								loading={approveMutation.isPending}
								onClose={() => setApproveOpen(false)}
								onConfirm={(notes) => {
									if (!enrollmentId || !instanceId) return;
									approveMutation.mutate(
										{
											enrollmentId,
											instanceId,
											body: notes ? { reviewNotes: notes } : {},
										},
										{
											onSuccess: () => {
												showSnackBar(
													`${detail.eventName ?? "Submission"} approved`,
													"success",
												);
												handleClose();
											},
											onError: (err) => {
												showSnackBar(
													(err as Error).message ?? "Approval failed",
													"error",
												);
											},
										},
									);
								}}
							/>
							<RejectDialog
								open={rejectOpen}
								eventName={detail.eventName}
								loading={rejectMutation.isPending}
								reasons={detail.rejectionReasons ?? []}
								consequences={detail.rejectionConsequences}
								onClose={() => setRejectOpen(false)}
								onConfirm={(rejectionReasonId, internalNote) => {
									if (!enrollmentId || !instanceId) return;
									rejectMutation.mutate(
										{
											enrollmentId,
											instanceId,
											body: { rejectionReasonId, internalNote },
										},
										{
											onSuccess: () => {
												showSnackBar(
													`${detail.eventName ?? "Submission"} rejected`,
													"success",
												);
												handleClose();
											},
											onError: (err) => {
												showSnackBar(
													(err as Error).message ?? "Rejection failed",
													"error",
												);
											},
										},
									);
								}}
							/>
						</Stack>
					);
				}}
			/>
		</>
	);
};

const ReviewBody = ({
	detail,
	stepIndex,
	onStep,
	reviewerId,
	onPlayed,
}: {
	detail: ReviewDetail;
	stepIndex: number;
	onStep: (index: number) => void;
	reviewerId: string;
	onPlayed: () => void;
}) => {
	const stepCount = detail.compoundSteps?.length ?? 1;
	const stepLabels = useMemo(() => {
		if (!detail.compoundSteps?.length)
			return [detail.eventName ?? "Submission"];
		return detail.compoundSteps.map((s, i) => s.name || `Step ${i + 1}`);
	}, [detail.compoundSteps, detail.eventName]);

	return (
		<Box>
			{stepCount > 1 && (
				<Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
					<Stepper activeStep={stepIndex} alternativeLabel>
						{stepLabels.map((label, idx) => (
							<Step
								key={`${label}-${idx}`}
								onClick={() => onStep(idx)}
								sx={{ cursor: "pointer" }}
							>
								<StepLabel>{label}</StepLabel>
							</Step>
						))}
					</Stepper>
					<Typography variant="caption" color="text.secondary">
						Step {stepIndex + 1} of {stepCount}: {stepLabels[stepIndex]}
					</Typography>
				</Box>
			)}
			{renderStepBody(detail, stepIndex, reviewerId, onPlayed)}
		</Box>
	);
};
